import React from 'react';
import { getTextColor } from '../../../../../style';
import styled from '@emotion/styled';
import PriceInput from '../../../Offers/PriceInput';
import colors from '../../../../../theme/colors';
import { isDarkMode } from '../../../../../utils/helpers';
import { CurrencySymbols } from '../../types';
import { CurrencyTipProps, OfferPriceType, PriceContainerProps } from './types/DashboardDiscoverModeOfferActionInput';
import { defaultOfferAmount } from '../../../../../common';
import { FONT_FAMILIES } from 'theme/material/constants';

const PriceContainer = styled('div')(
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  () => ({
    marginLeft: 12,
    marginRight: 12,
    textAlign: 'center',
    width: 185,
    height: 55,
    borderRadius: 6,
    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.16) inset',
    backgroundColor: isDarkMode() ? '#212121' : '#FFF',
    border: isDarkMode() ? '1px solid #212121' : '1px solid #DAE1E9',
    fontFamily: FONT_FAMILIES.secondary,
  }),
  ({ hasErrors }: PriceContainerProps) =>
    hasErrors && {
      border: '1px solid #cc5454',
    }
);

const setColor: Record<OfferPriceType, { color: string }> = {
  default: {
    color: 'grey',
  },
  brand: {
    color: '#3e79a5',
  },
  alert: { ...getTextColor().RED },
};
const CurrencyTip = styled.div<CurrencyTipProps>(
  {
    display: 'inline-flex',
    paddingLeft: 10,
    fontFamily: FONT_FAMILIES.secondary,
  },
  ({ offerPrice }) => ({
    ...(offerPrice ? setColor[offerPrice] : {}),
    ':before': {
      position: 'absolute',
      top: 14,
      right: '85%',
      fontSize: 16,
    },
  })
);

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const CurrencyLabelSpan = styled('span')(() => ({
  fontSize: 48,
  fontWeight: 400,
  letterSpacing: '-0.5px',
  lineHeight: '42px',
  color: isDarkMode() ? colors.blue['200'] : colors['light'].blue.tertiary,
  position: 'relative',
  top: 7,
  fontFamily: FONT_FAMILIES.secondary,
}));

const getInitialInputProps = () => ({
  style: {
    height: 55,
    fontSize: 48,
    lineHeight: '42px',
    letterSpacing: '-0.5px',
    fontWeight: 400,
    outline: 'none',
    border: 'none',
    marginLeft: -17,
    color: isDarkMode() ? colors.blue['200'] : colors['light'].blue.tertiary,
    fontFamily: FONT_FAMILIES.secondary,
  },
});

const adjustStylePerDigit = (number: number | null | undefined) => {
  const numString = number ? number.toString() : '0';
  let marginLeft = -17;
  let paddingLeft = 10;

  switch (numString.length) {
    case 3:
      marginLeft = -10;
      paddingLeft = 10;
      break;
    case 2:
      marginLeft = -22;
      paddingLeft = 20;
      break;
    case 1:
      marginLeft = -32;
      paddingLeft = 30;
      break;
    default:
      marginLeft = -32;
      paddingLeft = 10;
      break;
  }
  return {
    marginLeft,
    paddingLeft,
  };
};

interface Props {
  offerPrice?: number | null | undefined;
  updateOfferPrice: (offerPrice: string | number, hasError: boolean) => void;
  hasError: boolean;
  currencySymbols: CurrencySymbols;
}

const DashboardDiscoverModeOfferActionInput: React.FC<Props> = ({
  offerPrice,
  updateOfferPrice,
  hasError,
  currencySymbols,
}) => {
  const minValue = defaultOfferAmount.minValue;
  const maxValue = defaultOfferAmount.maxValue;

  const validateTextInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const Key = e.key;
    const regex = /[0-9]/;

    if (!regex.test(Key)) {
      if (e.preventDefault) e.preventDefault();
    }
  };

  const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = Number(e.target.value);
    const MaxLength = maxValue.toString().length;

    if (e.target.value.length <= MaxLength) {
      const isInvalid = Boolean(Number(inputValue) < Number(minValue) || Number(inputValue) > Number(maxValue));

      updateOfferPrice(inputValue, isInvalid);
    } else {
      e.preventDefault();
    }
  };

  const colorType = (price: boolean, hasErrors: boolean) => {
    switch (price) {
      case true:
        return 'default';
      case false:
        return !hasErrors ? 'brand' : 'alert';
      default:
        return;
    }
  };

  return (
    <PriceContainer
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      hasErrors={hasError}
    >
      <CurrencyTip
        offerPrice={colorType(offerPrice ? true : false, hasError)}
        style={{
          paddingLeft: adjustStylePerDigit(offerPrice).paddingLeft,
        }}
      >
        <CurrencyLabelSpan>
          {currencySymbols.currencyInitial}
          {currencySymbols.currencyLabel}
        </CurrencyLabelSpan>
        <PriceInput
          data-test-id="dashboard-discovermode-offer-price-input"
          hasErrors={hasError}
          value={offerPrice}
          onChange={inputOnChange}
          validateTextFn={validateTextInput}
          inputProps={{
            style: {
              ...getInitialInputProps().style,
              marginLeft: adjustStylePerDigit(offerPrice).marginLeft,
            },
          }}
          minWidth={100}
        />
      </CurrencyTip>
    </PriceContainer>
  );
};

export default DashboardDiscoverModeOfferActionInput;
